// Chatbot.js
import React, { useEffect, useMemo, useRef, useState } from "react";
import { useParams } from "react-router-dom";
import { createPortal } from "react-dom";
import { MessageSquare, Search, PlayCircle, RotateCcw, Mic, Volume2, Send, MessageCircle, X, Code } from "lucide-react";
import { resolveChatbotGreeting, formatAssistantReplyWithGreeting } from "../lib/chatbotGreeting";
import TalkingAvatar from "../components/TalkingAvatar";
import ChatbotWidget from "../components/ChatbotWidget";
import EmbedDeployModal from "../components/EmbedDeployModal";
import AvatarStill from "../images/AvatarStill.png";
import api, { agentApi } from "../services/api";

// D-ID streaming avatar (optional – only loaded when needed)
let dIdSdkPromise = null;
const getDIdSdk = async () => {
  if (!dIdSdkPromise) dIdSdkPromise = import("@d-id/client-sdk");
  const mod = await dIdSdkPromise;
  return mod?.createAgentManager ? mod : (mod?.default ?? mod);
};

// Generate a simple session id
const newSessionId = () =>
  `sess_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`;

// Darken a hex color by a factor 0–1 (e.g. 0.15 = 15% darker)
function darkenHex(hex, factor = 0.15) {
  const n = hex.replace(/^#/, "");
  if (!/^[0-9A-Fa-f]{6}$/.test(n)) return hex;
  const r = Math.max(0, parseInt(n.slice(0, 2), 16) * (1 - factor)) | 0;
  const g = Math.max(0, parseInt(n.slice(2, 4), 16) * (1 - factor)) | 0;
  const b = Math.max(0, parseInt(n.slice(4, 6), 16) * (1 - factor)) | 0;
  return `#${r.toString(16).padStart(2, "0")}${g.toString(16).padStart(2, "0")}${b.toString(16).padStart(2, "0")}`;
}

const Chatbot = ({
  widget = false,
  scriptId: widgetScriptId = null,
  widgetAvatarMode = "none",
  widgetVoiceOnly = false,
  embedMode = false,
  onClose = null,
  widgetTitle: embedTitle = "Chat with Maya",
  primaryColor: embedPrimaryColor = "#459EA3",
  initialScriptData = null,
  embedToken: initialEmbedToken = null,
  embedParentOrigin = null,
}) => {
  const { scriptId: routeScriptIdParam } = useParams(); // From /chatbot/:scriptId
  const routeScriptId = widget ? (widgetScriptId ?? routeScriptIdParam) : routeScriptIdParam;
  const [scripts, setScripts] = useState([]);
  const [filteredScripts, setFilteredScripts] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedScriptId, setSelectedScriptId] = useState("");
  const [selectedScriptName, setSelectedScriptName] = useState("");
  const [selectedScriptData, setSelectedScriptData] = useState(null);
  const [embedToken, setEmbedToken] = useState(initialEmbedToken);
  const [embedAccessError, setEmbedAccessError] = useState("");

  const [collections, setCollections] = useState([]);
  const [selectedCollectionIds, setSelectedCollectionIds] = useState([]);

  const [embedModalOpen, setEmbedModalOpen] = useState(false);
  const [sessionId, setSessionId] = useState("");
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [sending, setSending] = useState(false);
  const [isListening, setIsListening] = useState(false);
  const [playingAudioId, setPlayingAudioId] = useState(null);
  const [avatarVideoUrl, setAvatarVideoUrl] = useState(null);
  const [avatarStream, setAvatarStream] = useState(null); // WebRTC MediaStream from D-ID
  const [avatarVideoLoading, setAvatarVideoLoading] = useState(false);
  const [useStreamingAvatar, setUseStreamingAvatar] = useState(false); // when true, Read aloud + auto-play use D-ID stream
  const [avatarMode, setAvatarMode] = useState("none"); // 'none' | 'image' | 'video' – default no avatar, full chat
  const [autoPlayEnabled, setAutoPlayEnabled] = useState(false);
  const lastAutoPlayedIndexRef = useRef(-1);
  const widgetVideoSessionStartedRef = useRef(false);
  const initialGreetingPlayedRef = useRef(false);
  const bottomRef = useRef(null);
  const recognitionRef = useRef(null);
  const stopTimeoutRef = useRef(null);
  const audioRef = useRef(null);
  const messagesEndRef = useRef(null);
  const dIdAgentManagerRef = useRef(null);
  const dIdLastErrorRef = useRef(null);
  const dIdStreamReadyRef = useRef(false);
  const dIdStreamReadyPromiseRef = useRef(null);
  const dIdStreamReadyResolveRef = useRef(null);
  const hasSessionSavedRef = useRef(false);
  const initialEmbedBootstrappedRef = useRef(false);

  // Refs to store latest state values for speech recognition handler
  const selectedScriptDataRef = useRef(null);
  const selectedCollectionIdsRef = useRef([]);
  const routeScriptIdRef = useRef(null);
  const messagesRef = useRef([]);
  const embedTokenRef = useRef(initialEmbedToken);
  const embedParentOriginRef = useRef(embedParentOrigin);
  // Persist collections when session starts so AI always knows which KB to use for the session
  const sessionCollectionIdsRef = useRef(null);
  
  // Keep refs in sync with state
  useEffect(() => {
    selectedScriptDataRef.current = selectedScriptData;
    selectedCollectionIdsRef.current = selectedCollectionIds;
    routeScriptIdRef.current = routeScriptId;
    messagesRef.current = messages;
    embedTokenRef.current = embedToken;
    embedParentOriginRef.current = embedParentOrigin;
  }, [selectedScriptData, selectedCollectionIds, routeScriptId, messages, embedToken, embedParentOrigin]);

  useEffect(() => {
    setEmbedToken(initialEmbedToken || null);
  }, [initialEmbedToken]);

  useEffect(() => {
    initialEmbedBootstrappedRef.current = false;
  }, [widgetScriptId, initialScriptData?._id]);

  const saveSessionLog = async ({ silent = false, useBeacon = false } = {}) => {
    const currentMessages = Array.isArray(messagesRef.current) ? messagesRef.current : [];
    if (currentMessages.length <= 1 || hasSessionSavedRef.current) return false;

    const apiBase = process.env.REACT_APP_AGENT_API_URL || '';
    const isPublicEmbed = !!embedMode;
    const payload = {
      scriptData: selectedScriptDataRef.current || null,
      scriptId: selectedScriptDataRef.current?._id || routeScriptIdRef.current || null,
      embedToken: embedTokenRef.current || null,
      parentOrigin: embedParentOriginRef.current || null,
      messages: currentMessages,
      callSidType: isPublicEmbed ? 'chatbot-public' : 'chatbot'
    };
    const endpoint = isPublicEmbed ? '/api/chatbot-session-public' : '/api/chatbot-session';

    // Best-effort path for unload/navigation events
    if (useBeacon && typeof navigator !== 'undefined' && typeof navigator.sendBeacon === 'function') {
      try {
        const blob = new Blob([JSON.stringify(payload)], { type: 'application/json' });
        const ok = navigator.sendBeacon(`${apiBase}${endpoint}`, blob);
        if (ok) {
          hasSessionSavedRef.current = true;
          return true;
        }
      } catch (_) {
        // Fallback to fetch below
      }
    }

    try {
      const token = localStorage.getItem('token');
      const res = await fetch(`${apiBase}${endpoint}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(!isPublicEmbed && token ? { Authorization: `Bearer ${token}` } : {})
        },
        body: JSON.stringify(payload),
        keepalive: useBeacon
      });
      const data = await res.json().catch(() => ({}));
      if (res.ok && data.success) {
        hasSessionSavedRef.current = true;
        if (!silent) alert('Session saved successfully!');
        return true;
      }
      if (!silent) {
        console.error('Failed to save session:', data?.message);
        alert('Failed to save session. Please try again.');
      }
      return false;
    } catch (err) {
      if (!silent) {
        console.error('Error saving session:', err);
        alert('Error saving session. Please try again.');
      }
      return false;
    }
  };

  useEffect(() => {
    const onPageHide = () => {
      // Fire-and-forget save when tab/page is leaving.
      saveSessionLog({ silent: true, useBeacon: true });
    };
    const onBeforeUnload = () => {
      saveSessionLog({ silent: true, useBeacon: true });
    };
    const onVisibilityChange = () => {
      if (document.visibilityState === 'hidden') {
        saveSessionLog({ silent: true, useBeacon: true });
      }
    };
    window.addEventListener('pagehide', onPageHide);
    window.addEventListener('beforeunload', onBeforeUnload);
    document.addEventListener('visibilitychange', onVisibilityChange);
    return () => {
      window.removeEventListener('pagehide', onPageHide);
      window.removeEventListener('beforeunload', onBeforeUnload);
      document.removeEventListener('visibilitychange', onVisibilityChange);
      // Component unmount fallback
      saveSessionLog({ silent: true, useBeacon: true });
    };
    // Intentionally once; refs carry latest state.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (widget) {
      if (widgetScriptId) {
        const canBootstrapEmbed =
          embedMode &&
          initialScriptData &&
          String(initialScriptData._id || "") === String(widgetScriptId);
        if (canBootstrapEmbed && !initialEmbedBootstrappedRef.current) {
          initialEmbedBootstrappedRef.current = true;
          setSelectedScriptId(widgetScriptId);
          setSelectedScriptName(initialScriptData.scriptName || "");
          setSelectedScriptData(initialScriptData);
          setEmbedToken(initialEmbedToken || null);
          setEmbedAccessError("");
          const sid = newSessionId();
          setSessionId(sid);
          sessionCollectionIdsRef.current = [];
          setMessages([
            {
              role: "assistant",
              content: resolveChatbotGreeting(initialScriptData),
            },
          ]);
        } else if (!canBootstrapEmbed) {
          loadScript(widgetScriptId, true);
        }
      }
    } else {
      if (!routeScriptId) {
        fetchScripts();
        fetchCollections();
      } else {
        loadScript(routeScriptId, true);
      }
    }
  }, [routeScriptId, widget, widgetScriptId, embedMode, initialScriptData, initialEmbedToken]);

  // In widget, use streaming avatar when widgetAvatarMode is 'video' or widgetVoiceOnly (voice-only uses video avatar)
  const effectiveWidgetVideo = widget && (widgetAvatarMode === "video" || widgetVoiceOnly);
  useEffect(() => {
    if (!effectiveWidgetVideo) return;
    setUseStreamingAvatar(true);
    ensureDIdConnected();
  }, [effectiveWidgetVideo]);

  // Widget D-ID stream mode: auto-start session (greeting) and play first message
  useEffect(() => {
    if (!widget || !(widgetAvatarMode === "video" || widgetVoiceOnly)) return;
    const hasKb = Array.isArray(selectedCollectionIds) && selectedCollectionIds.length > 0;
    if (!selectedScriptData && !hasKb) return;
    if (messages.length > 0) return;
    if (widgetVideoSessionStartedRef.current) return;
    widgetVideoSessionStartedRef.current = true;
    startSession();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [widget, widgetAvatarMode, selectedScriptData, selectedCollectionIds, messages.length]);

  useEffect(() => {
    if (!widget || !(widgetAvatarMode === "video" || widgetVoiceOnly)) return;
    if (messages.length !== 1 || messages[0]?.role !== "assistant") return;
    if (initialGreetingPlayedRef.current) return;
    initialGreetingPlayedRef.current = true;
    const t = setTimeout(() => playAIResponse(messages[0].content, 0), 50);
    return () => clearTimeout(t);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [widget, widgetAvatarMode, widgetVoiceOnly, messages]);

  const [widgetOpen, setWidgetOpen] = useState(false);
  const [popupScriptId, setPopupScriptId] = useState("");
  const [popupAvatarMode, setPopupAvatarMode] = useState("none"); // 'none' | 'image' | 'video'
  const [widgetOpenDropdown, setWidgetOpenDropdown] = useState(""); // for left-panel "Open in widget" select

  const fetchScripts = async () => {
    try {
      const token = localStorage.getItem('token');
      const apiBase = process.env.REACT_APP_AGENT_API_URL || '';
      const currentUser = (() => {
        try { return JSON.parse(localStorage.getItem('user') || 'null'); } catch { return null; }
      })();
      const res = await fetch(`${apiBase}/api/scripts`, {
        headers: {
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
      });
      const data = await res.json();
      if (data.success) {
        let list = Array.isArray(data.scripts) ? data.scripts : [];
        if (currentUser && Number(currentUser.userlevel) === 100) {
          const myId = currentUser.id || currentUser._id || currentUser.userId;
          list = list.filter((s) => {
            const owner = typeof s.ownerId === "string" ? s.ownerId.trim().toLowerCase() : s.ownerId;
            return owner === "global" || String(s.ownerId) === String(myId);
          });
        }
        setScripts(list);
        setFilteredScripts(list);
      }
    } catch (err) {
      console.error("❌ Error fetching scripts:", err);
    }
  };

  const fetchCollections = async () => {
    try {
      const token = localStorage.getItem('token');
      const apiBase = process.env.REACT_APP_AGENT_API_URL || '';
      const currentUser = (() => {
        try { return JSON.parse(localStorage.getItem('user') || 'null'); } catch { return null; }
      })();
      const ownerId = currentUser?.id || currentUser?._id || currentUser?.userId || null;
      const query = ownerId ? `?ownerId=${encodeURIComponent(ownerId)}` : '';
      const res = await fetch(`${apiBase}/api/knowledgebase-collections${query}`, {
        headers: { ...(token ? { Authorization: `Bearer ${token}` } : {}) }
      });
      const data = await res.json();
      if (data.success && Array.isArray(data.collections)) {
        setCollections(data.collections);
      }
    } catch (err) {
      console.error("❌ Error fetching collections:", err);
    }
  };

  const handleSearch = (term) => {
    setSearchTerm(term);
    const filtered = scripts.filter((s) =>
      s.scriptName.toLowerCase().includes(term.toLowerCase())
    );
    setFilteredScripts(filtered);
  };

  const loadScript = async (_id, autoStart = false) => {
    try {
      const token = localStorage.getItem('token');
      const apiBase = process.env.REACT_APP_AGENT_API_URL || '';
      const useEmbedApi = embedMode;
      const params = new URLSearchParams();
      if (useEmbedApi && embedParentOrigin) {
        params.set("parentOrigin", embedParentOrigin);
      }
      const path = useEmbedApi ? `/api/script-embed/${_id}` : `/api/script/${_id}`;
      const res = await fetch(`${apiBase}${path}${params.toString() ? `?${params.toString()}` : ""}`, {
        headers: useEmbedApi ? {} : { ...(token ? { Authorization: `Bearer ${token}` } : {}) },
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok || !data?.success) {
        const message = data?.message || `Failed to load script (${res.status})`;
        if (useEmbedApi) {
          setEmbedAccessError(message);
        }
        throw new Error(message);
      }

      setSelectedScriptId(_id);
      setSelectedScriptName(data.script.scriptName);
      setSelectedScriptData(data.script);
      if (useEmbedApi) {
        setEmbedToken(data.embedToken || null);
        setEmbedAccessError("");
      }

      if (autoStart) {
        const sid = newSessionId();
        setSessionId(sid);
        sessionCollectionIdsRef.current = [];
        setMessages([
          {
            role: "assistant",
            content: resolveChatbotGreeting(data.script),
          },
        ]);
      }
    } catch (err) {
      console.error("❌ Error loading script:", err);
    }
  };

  const formatDate = (isoDate) => new Date(isoDate).toLocaleString();

  const canSend = useMemo(() => {
    // Allow sending if we have input and either:
    // 1. A script is selected (selectedScriptData exists)
    // 2. Knowledge base: collections or single file selected
    // 3. We're in clean mode with routeScriptId (script loaded automatically)
    const hasScript = selectedScriptData || routeScriptId;
    const hasKb = Array.isArray(selectedCollectionIds) && selectedCollectionIds.length > 0;
    return (
      input.trim().length > 0 && (hasScript || hasKb)
    );
  }, [input, selectedScriptData, selectedCollectionIds, routeScriptId]);

  const scrollToBottom = () =>
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  useEffect(() => {
    scrollToBottom();
  }, [messages, sending]);

  // Initialize Speech Recognition
  useEffect(() => {
    if (!('webkitSpeechRecognition' in window) && !('SpeechRecognition' in window)) {
      console.warn('Speech recognition not supported in this browser');
      return;
    }

    const SpeechRecognition = window.SpeechRecognition || window.webkitSpeechRecognition;
    const recognition = new SpeechRecognition();
    recognition.continuous = false;
    recognition.interimResults = false;
    recognition.lang = 'en-US';

    recognition.onstart = () => {
      console.log('Speech recognition started');
      setIsListening(true);
    };

    recognition.onresult = (event) => {
      if (stopTimeoutRef.current) {
        clearTimeout(stopTimeoutRef.current);
        stopTimeoutRef.current = null;
      }

      const transcript = event.results[event.resultIndex][0].transcript;
      console.log('Speech recognition result:', transcript);
      if (transcript.trim()) {
        // Set the input text and auto-send it
        setInput(transcript);
        setIsListening(false);
        
        // Auto-send after a short delay to ensure input state is updated
        // Use refs (useRefs=true) to get the latest state values and avoid stale closures
        setTimeout(() => {
          // Send the message using the transcript directly with useRefs=true
          // This ensures we use the latest state values from refs
          sendMessage(transcript.trim(), true);
        }, 200);
      } else {
        setIsListening(false);
      }
    };

    recognition.onerror = (event) => {
      console.error('Speech recognition error:', event.error);
      setIsListening(false);
      if (event.error === 'no-speech') {
        console.log('No speech detected');
        setInput("");
      } else if (event.error === 'audio-capture') {
        console.error('No microphone found or permission denied');
        alert('Microphone access required for voice input. Please check your browser permissions.');
      } else if (event.error === 'not-allowed') {
        console.error('Microphone permission denied');
        alert('Microphone permission denied. Please enable microphone access in your browser settings.');
      }
    };

    recognition.onend = () => {
      console.log('Speech recognition ended');
      if (stopTimeoutRef.current) {
        clearTimeout(stopTimeoutRef.current);
        stopTimeoutRef.current = null;
      }
      setIsListening(false);
    };

    recognitionRef.current = recognition;

    return () => {
      if (stopTimeoutRef.current) {
        clearTimeout(stopTimeoutRef.current);
        stopTimeoutRef.current = null;
      }
      if (recognitionRef.current) {
        try {
          recognitionRef.current.stop();
        } catch (e) {
          // Ignore errors on cleanup
        }
      }
    };
  }, []);

  // Auto-play AI responses when enabled (or always in widget D-ID stream / voice-only mode)
  const effectiveAutoPlay = autoPlayEnabled || (widget && (widgetAvatarMode === "video" || widgetVoiceOnly));
  useEffect(() => {
    if (!effectiveAutoPlay || messages.length === 0) return;
    const lastIndex = messages.length - 1;
    const lastMessage = messages[lastIndex];
    const isNewAssistant = lastMessage.role === "assistant" && lastIndex > lastAutoPlayedIndexRef.current;
    const widgetVideoOrVoice = widget && (widgetAvatarMode === "video" || widgetVoiceOnly);
    if (isNewAssistant && (lastIndex > 0 || widgetVideoOrVoice)) {
      if (lastIndex === 0 && widgetVideoOrVoice) {
        // First message already played by initialGreetingPlayedRef effect; just mark it
        lastAutoPlayedIndexRef.current = 0;
        return;
      }
      lastAutoPlayedIndexRef.current = lastIndex;
      setTimeout(() => playAIResponse(lastMessage.content, lastIndex), 50);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [messages, effectiveAutoPlay]);

  // Cleanup audio on unmount
  useEffect(() => {
    return () => {
      if (audioRef.current) {
        audioRef.current.pause();
        audioRef.current = null;
      }
    };
  }, []);

  const startSession = () => {
    const hasKb = Array.isArray(selectedCollectionIds) && selectedCollectionIds.length > 0;
    if (!selectedScriptData && !hasKb) {
      alert("Select a script or knowledge base collections to start a session.");
      return;
    }
    const sid = newSessionId();
    hasSessionSavedRef.current = false;
    setSessionId(sid);
    sessionCollectionIdsRef.current = Array.isArray(selectedCollectionIds) ? [...selectedCollectionIds] : [];

    const collectionNames = collections
      .filter((c) => selectedCollectionIds?.includes(String(c._id || c.id)))
      .map((c) => c.name || c.collectionName)
      .filter(Boolean);
    const kbLabel = collectionNames.length > 0 ? collectionNames.join(", ") : "the documents";

    setMessages([
      {
        role: "assistant",
        content: selectedScriptData
          ? resolveChatbotGreeting(selectedScriptData)
          : formatAssistantReplyWithGreeting(null, `Ask me anything about ${kbLabel}.`),
      },
    ]);
    setInput("");
  };

  const sendMessage = async (messageText = null, useRefs = false) => {
    const userMsg = messageText || input.trim();
    if (!userMsg || sending) return;
    
    // Use refs if called from microphone (to avoid stale closure), otherwise use state
    const currentScriptData = useRefs ? selectedScriptDataRef.current : selectedScriptData;
    const currentRouteScriptId = useRefs ? routeScriptIdRef.current : routeScriptId;
    const currentMessages = useRefs ? messagesRef.current : messages;
    // Use session-locked collections if set (from when session started), else current selection
    const sessionLocked = sessionCollectionIdsRef.current && sessionCollectionIdsRef.current.length > 0;
    const currentCollectionIds = sessionLocked
      ? sessionCollectionIdsRef.current
      : (useRefs ? selectedCollectionIdsRef.current : selectedCollectionIds);
    // On first message, lock in collections for the session if we have them
    if (!sessionLocked && Array.isArray(useRefs ? selectedCollectionIdsRef.current : selectedCollectionIds) && (useRefs ? selectedCollectionIdsRef.current : selectedCollectionIds).length > 0) {
      sessionCollectionIdsRef.current = [...(useRefs ? selectedCollectionIdsRef.current : selectedCollectionIds)];
    }
    
    // Log current state for debugging
    console.log('[Chatbot] sendMessage called:', {
      source: messageText ? 'microphone' : 'typing',
      useRefs,
      hasSelectedScriptData: !!currentScriptData,
        selectedScriptId,
        selectedScriptName,
        hasCollectionIds: (currentCollectionIds || []).length,
        routeScriptId: currentRouteScriptId,
      messagesCount: currentMessages.length,
      selectedScriptDataKeys: currentScriptData ? Object.keys(currentScriptData) : []
    });
    
    // Determine scriptData to send
    // Backend expects scriptData to have a 'script' property (string), but we'll send it anyway
    // and let the backend validate - some scripts might have different structures
    let scriptDataToSend = currentScriptData || null;
    
    // If scriptData exists but doesn't have 'script' property, log a warning but still send it
    // The backend will handle validation
    if (scriptDataToSend && !scriptDataToSend.script) {
      console.warn('[Chatbot] selectedScriptData exists but missing script property:', {
        hasScript: !!scriptDataToSend.script,
        scriptType: typeof scriptDataToSend.script,
        scriptDataKeys: Object.keys(scriptDataToSend),
        note: 'Sending anyway - backend will validate'
      });
    }
    
    // Validate that we have either a script selected OR a fileId OR routeScriptId
    // Also allow sending if a session has already been started (messages exist)
    // We'll send scriptData even if it doesn't have the script property - backend will handle it
    const hasCollectionIds = Array.isArray(currentCollectionIds) && currentCollectionIds.length > 0;
    const hasValidConfig = scriptDataToSend || hasCollectionIds || currentRouteScriptId;
    const hasActiveSession = currentMessages.length > 0;
    
    if (!hasValidConfig && !hasActiveSession) {
      const errorMsg = "Please select a script or knowledge base and start a session before sending messages.";
      console.error('[Chatbot] Validation failed - no script/knowledge base and no active session:', {
        hasSelectedScriptData: !!currentScriptData,
        selectedScriptId,
        hasCollectionIds: (currentCollectionIds || []).length,
        routeScriptId: currentRouteScriptId,
        messagesCount: currentMessages.length,
        source: messageText ? 'microphone' : 'typing',
        useRefs
      });
      alert(errorMsg);
      return;
    }
    
    // If we have an active session but no script/fileId, use what was used before
    // (This handles the case where user started a session and then tries to send more messages)
    if (hasActiveSession && !hasValidConfig) {
      console.log('[Chatbot] Using existing session configuration (script/collections from previous messages)');
    }
    
    setInput("");
    const newHistory = [...currentMessages, { role: "user", content: userMsg }];
    setMessages(newHistory);
    setSending(true);

    try {
      const apiBase = process.env.REACT_APP_AGENT_API_URL || '';
      const token = localStorage.getItem('token');
      const isPublicEmbed = !!embedMode;
      const endpoint = isPublicEmbed ? '/api/chatbot-public' : '/api/chatbot';
      
      console.log('[Chatbot] Sending | query:', JSON.stringify(userMsg.substring(0, 80)), '| collections:', (currentCollectionIds || []).length, '| script:', !!scriptDataToSend);
      
      const res = await fetch(`${apiBase}${endpoint}`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          ...(!isPublicEmbed && token ? { Authorization: `Bearer ${token}` } : {})
        },
        body: JSON.stringify({
          sessionId,
          scriptId: scriptDataToSend?._id || currentRouteScriptId || null,
          scriptData: scriptDataToSend,
          embedToken: embedTokenRef.current || null,
          parentOrigin: embedParentOriginRef.current || null,
          collectionIds: hasCollectionIds ? currentCollectionIds : null,
          query: userMsg,
          messages: newHistory,
        }),
      });

      const data = await res.json();
      if (!res.ok || !data?.success) {
        const errorMsg = data?.message || `Request failed with status ${res.status}`;
        if (embedMode && typeof data?.code === "string" && data.code.startsWith("EMBED_")) {
          setEmbedAccessError(errorMsg);
        }
        console.error('❌ Chatbot API error:', {
          status: res.status,
          errorMsg,
          response: data,
          requestBody: {
            hasScriptData: !!scriptDataToSend,
            hasScriptProperty: !!scriptDataToSend?.script,
            hasCollectionIds: (selectedCollectionIds || []).length,
            messageCount: newHistory.length
          }
        });
        setMessages((m) => [
          ...m,
          {
            role: "assistant",
            content: formatAssistantReplyWithGreeting(
              scriptDataToSend,
              `Sorry, I encountered an error: ${errorMsg}. Please make sure a script or knowledge base is selected.`,
            ),
          },
        ]);
      } else {
        const rag = data.rag || null;
        if (rag?.hadKb) {
          console.log("[Chatbot] RAG:", rag.usedRag ? `Used KB (${rag.chunkCount} chunks${rag.sources?.length ? ` from: ${rag.sources.join(", ")}` : ""})` : "No relevant chunks found");
        }
        setMessages((m) => [...m, {
          role: "assistant",
          content: formatAssistantReplyWithGreeting(scriptDataToSend, data.reply),
          rag
        }]);
      }
    } catch (err) {
      console.error('❌ Chatbot network error:', err);
      setMessages((m) => [
        ...m,
        {
          role: "assistant",
          content: formatAssistantReplyWithGreeting(
            scriptDataToSend,
            `Network error: ${err.message || 'Please try again.'}`,
          ),
        },
      ]);
    } finally {
      setSending(false);
    }
  };

  // Start/stop voice recording (push-to-talk)
  const startListening = () => {
    if (!recognitionRef.current || sending) {
      console.log('Cannot start listening:', { hasRecognition: !!recognitionRef.current, sending });
      return;
    }

    if (isListening) {
      console.log('Already listening, ignoring start request');
      return;
    }

    try {
      console.log('Starting speech recognition...');
      recognitionRef.current.start();
    } catch (err) {
      console.error('Failed to start speech recognition:', err);
      setIsListening(false);
      if (err.name === 'InvalidStateError' && err.message.includes('already started')) {
        console.log('Recognition already started, setting state to listening');
        setIsListening(true);
      }
    }
  };

  const stopListening = () => {
    if (!recognitionRef.current || !isListening) return;

    if (stopTimeoutRef.current) {
      clearTimeout(stopTimeoutRef.current);
      stopTimeoutRef.current = null;
    }

    try {
      console.log('Stopping speech recognition...');
      stopTimeoutRef.current = setTimeout(() => {
        try {
          recognitionRef.current?.stop();
          stopTimeoutRef.current = null;
        } catch (err) {
          console.error('Error stopping speech recognition in timeout:', err);
          setIsListening(false);
        }
      }, 300);
    } catch (err) {
      console.error('Error stopping speech recognition:', err);
      setIsListening(false);
    }
  };

  // Play AI response: Kokoro TTS when no avatar; D-ID streaming when avatar enabled
  const playAIResponse = async (text, messageId) => {
    if (playingAudioId === messageId) {
      if (audioRef.current) {
        audioRef.current.pause();
        audioRef.current.currentTime = 0;
      }
      setPlayingAudioId(null);
      return;
    }

    // D-ID SDK may throw when its internal Mixpanel analytics is blocked (e.g. ad blocker); ignore that.
    const isAnalyticsOnlyError = (err) => {
      const msg = err?.message ?? String(err);
      return /Analytics tracking error|mixpanel|ERR_BLOCKED_BY_ADBLOCKER/i.test(msg);
    };

    if (useStreamingAvatar) {
      setPlayingAudioId(messageId);
      const textSlice = text.slice(0, 4000);
      const manager = await ensureDIdConnected();
      if (manager) {
        setAvatarVideoLoading(true);
        try {
          await waitForDIdStreamReady();
          await manager.speak({ type: "text", input: textSlice });
        } catch (err) {
          if (isAnalyticsOnlyError(err)) {
            // Speak likely succeeded; analytics (e.g. Mixpanel) was blocked
            return;
          }
          console.warn("D-ID speak failed:", err);
          setPlayingAudioId(null);
          alert(err?.message || "Failed to play avatar");
        } finally {
          setAvatarVideoLoading(false);
        }
      } else {
        setPlayingAudioId(null);
        alert(dIdLastErrorRef.current || "Streaming avatar not available.");
      }
      return;
    }

    // Kokoro TTS mode (fallback to D-ID streaming if Kokoro fails)
    try {
      setPlayingAudioId(messageId);
      if (audioRef.current) {
        audioRef.current.pause();
        audioRef.current.currentTime = 0;
      }

      try {
        const { data } = await agentApi.post('/api/kokoro-tts', { text });
        if (!data?.success) {
          throw new Error(data?.message || 'Failed to generate audio');
        }

        const audioBlob = await fetch(`data:audio/${data.format || 'wav'};base64,${data.audio}`).then(r => r.blob());
        const audioUrl = URL.createObjectURL(audioBlob);

        const audio = new Audio(audioUrl);
        audioRef.current = audio;

        audio.onended = () => {
          setPlayingAudioId(null);
          URL.revokeObjectURL(audioUrl);
        };

        audio.onerror = () => {
          setPlayingAudioId(null);
          URL.revokeObjectURL(audioUrl);
          console.error('Error playing audio');
        };

        await audio.play();
      } catch (kokoroErr) {
        const status = kokoroErr?.response?.status;
        const backendMsg = kokoroErr?.response?.data?.message || kokoroErr?.message;
        console.warn('Kokoro TTS failed:', backendMsg, kokoroErr);

        // Widget "No Avatar" and "Still image avatar" use Kokoro only; no D-ID fallback
        const widgetKokoroOnly = widget && (widgetAvatarMode === 'none' || widgetAvatarMode === 'image');
        if (widgetKokoroOnly) {
          setPlayingAudioId(null);
          alert(backendMsg || "TTS failed. Please try again.");
          return;
        }

        // Fallback: try D-ID streaming avatar if Kokoro is down (main page or widget video mode)
        try {
          const manager = await ensureDIdConnected();
          if (manager) {
            setUseStreamingAvatar(true);
            await waitForDIdStreamReady();
            await manager.speak({ type: "text", input: text.slice(0, 4000) });
          } else {
            setPlayingAudioId(null);
            alert(status === 500 ? `TTS error: ${backendMsg || "Kokoro API may be unreachable (check KOKORO_API_URL, gpu.freshroad.com)."}` : (backendMsg || "Failed to play audio."));
          }
        } catch (dIdErr) {
          if (isAnalyticsOnlyError(dIdErr)) return;
          setPlayingAudioId(null);
          alert(status === 500 ? `TTS error: ${backendMsg || "Kokoro unreachable."} D-ID fallback also failed.` : (backendMsg || "Failed to play audio."));
        }
      }
    } catch (err) {
      console.error('Error playing AI response:', err);
      setPlayingAudioId(null);
      alert(err?.response?.data?.message || err?.message || 'Failed to play audio. Please try again.');
    }
  };

  const resetSelections = () => {
    setSelectedScriptId("");
    setSelectedScriptName("");
    setSelectedScriptData(null);
    setSelectedCollectionIds([]);
    sessionCollectionIdsRef.current = null;
  };

  const resetChat = () => {
    hasSessionSavedRef.current = false;
    setSessionId("");
    setMessages([]);
    setInput("");
    sessionCollectionIdsRef.current = null;
    setAvatarVideoUrl(null);
    setAvatarStream(null);
    setUseStreamingAvatar(false);
    if (dIdAgentManagerRef.current) {
      dIdAgentManagerRef.current.disconnect();
      dIdAgentManagerRef.current = null;
    }
    dIdStreamReadyRef.current = false;
    dIdStreamReadyPromiseRef.current = null;
    dIdStreamReadyResolveRef.current = null;
  };

  const waitForDIdStreamReady = () => {
    if (dIdStreamReadyRef.current) return Promise.resolve();
    if (dIdStreamReadyPromiseRef.current) {
      return Promise.race([
        dIdStreamReadyPromiseRef.current,
        new Promise((r) => setTimeout(r, 10000)),
      ]);
    }
    return Promise.resolve();
  };

  // Connect to D-ID Streaming Avatar. Returns agent manager or null if not available.
  const ensureDIdConnected = async () => {
    dIdLastErrorRef.current = null;
    if (dIdAgentManagerRef.current) return dIdAgentManagerRef.current;
    let sdk;
    try {
      sdk = await getDIdSdk();
    } catch (_) {
      dIdLastErrorRef.current = "D-ID SDK not loaded.";
      return null;
    }
    if (!sdk?.createAgentManager) {
      dIdLastErrorRef.current = "D-ID SDK missing createAgentManager.";
      return null;
    }
    try {
      const { data } = await api.get("/d-id/credentials");
      if (!data?.success || !data?.agentId || !data?.clientKey) {
        dIdLastErrorRef.current = data?.error || "No D-ID credentials from server.";
        return null;
      }
      dIdStreamReadyRef.current = false;
      dIdStreamReadyPromiseRef.current = new Promise((resolve) => {
        dIdStreamReadyResolveRef.current = resolve;
      });
      const callbacks = {
        onSrcObjectReady(value) {
          if (value) {
            setAvatarStream(value);
            dIdStreamReadyRef.current = true;
            dIdStreamReadyResolveRef.current?.();
            dIdStreamReadyResolveRef.current = null;
          }
          return value;
        },
        onVideoStateChange(state) {
          if (state === "STOP") setPlayingAudioId(null);
        },
        onConnectionStateChange(state) {
          if (state === "disconnected" || state === "closed" || state === "fail") {
            setAvatarStream(null);
            dIdAgentManagerRef.current = null;
            dIdStreamReadyRef.current = false;
            dIdStreamReadyPromiseRef.current = null;
            dIdStreamReadyResolveRef.current = null;
          }
        },
        onError(error, errorData) {
          const msg = errorData?.description ?? error?.message ?? String(error);
          dIdLastErrorRef.current = msg;
          console.warn("D-ID error:", error, errorData);
        },
      };
      const streamOptions = { streamWarmup: true, compatibilityMode: "auto" };
      const agentManager = await sdk.createAgentManager(data.agentId, {
        auth: { type: "key", clientKey: data.clientKey },
        callbacks,
        streamOptions,
      });
      await agentManager.connect();
      dIdAgentManagerRef.current = agentManager;
      return agentManager;
    } catch (err) {
      const msg = err?.response?.data?.error ?? err?.message ?? "Connection failed.";
      dIdLastErrorRef.current = msg;
      console.warn("D-ID stream connect failed:", err);
      return null;
    }
  };

  // true = streaming avatar (OpenAI text -> D-ID SDK speak). false = D-ID Talks API clip.
  const useStreamingAvatarMode = true;

  const generateAvatarVideo = async () => {
    const lastAssistant = [...messages].reverse().find((m) => m.role === "assistant");
    const text = lastAssistant?.content?.slice(0, 4000) || "";
    if (!text) {
      alert("No assistant message to turn into video.");
      return;
    }
    setAvatarVideoLoading(true);
    setAvatarVideoUrl(null);

    if (useStreamingAvatarMode) {
      const manager = await ensureDIdConnected();
      if (manager) {
        try {
          setUseStreamingAvatar(true);
          const lastIndex = messages.findIndex((m) => m === lastAssistant);
          setPlayingAudioId(lastIndex >= 0 ? lastIndex : messages.length - 1);
          await waitForDIdStreamReady();
          await manager.speak({ type: "text", input: text });
        } catch (err) {
          console.warn("D-ID speak failed:", err);
          setPlayingAudioId(null);
          alert(err?.message || "Failed to start avatar");
        } finally {
          setAvatarVideoLoading(false);
        }
      } else {
        setAvatarVideoLoading(false);
        alert(dIdLastErrorRef.current || "Streaming avatar not available.");
      }
      return;
    }

    // Non-streaming: create a talk clip and show the result URL in the header.
    try {
      const { data } = await api.post("/d-id/create-talk", { text });
      if (data?.success && data?.videoUrl) {
        const lastIndex = messages.findIndex((m) => m === lastAssistant);
        setPlayingAudioId(lastIndex >= 0 ? lastIndex : messages.length - 1);
        setAvatarVideoUrl(data.videoUrl);
      } else {
        alert(data?.error || "Failed to create avatar video.");
      }
    } catch (err) {
      const msg = err?.response?.data?.error || err?.message || "Failed to create avatar video.";
      alert(msg);
    } finally {
      setAvatarVideoLoading(false);
    }
  };

  const endSession = async () => {
    // Stop any playing audio immediately
    if (audioRef.current) {
      audioRef.current.pause();
      audioRef.current.currentTime = 0;
      audioRef.current = null;
    }
    setPlayingAudioId(null);
    setAvatarVideoUrl(null);
    setAvatarStream(null);
    setUseStreamingAvatar(false);
    if (dIdAgentManagerRef.current) {
      dIdAgentManagerRef.current.disconnect();
      dIdAgentManagerRef.current = null;
    }

    // Stop speech recognition if active
    if (recognitionRef.current && isListening) {
      try {
        recognitionRef.current.stop();
      } catch (e) {
        // Ignore errors
      }
    }
    setIsListening(false);

    if (messages.length <= 1) {
      // Only has the initial greeting, no need to save
      resetChat();
      return;
    }

    try {
      await saveSessionLog({ silent: false, useBeacon: false });
    } catch (_) {
      // saveSessionLog already handles user-facing errors
    } finally {
      // Reset chat regardless of save result
      resetChat();
      hasSessionSavedRef.current = false;
    }
  };

  // === UI (new style: teal/off-white) ===
  const rootClass = widget
    ? "h-full w-full min-h-0 flex flex-col overflow-hidden bg-[#F5F7FA]"
    : `relative isolate flex flex-col ${routeScriptId ? "min-h-screen bg-[#F5F7FA] flex items-center justify-center p-4" : "h-full min-h-0 bg-[#F5F7FA] overflow-hidden"}`;
  const innerClass = widget
    ? "relative flex flex-1 min-h-0 min-w-0 gap-3 overflow-hidden"
    : `relative flex flex-1 min-h-0 min-w-0 flex-col md:flex-row ${routeScriptId ? "w-full max-w-6xl" : "p-4 md:p-6"} gap-4 md:gap-6 overflow-hidden`;
  const leftPanelClass = widget ? "w-56 shrink-0 flex flex-col min-h-0 rounded-xl bg-white border border-gray-200/80 shadow-sm overflow-hidden" : "w-full md:w-80 lg:w-96 shrink-0 flex flex-col min-h-0 min-w-0 rounded-2xl bg-white border border-gray-200/80 shadow-sm overflow-hidden";
  const chatPanelClass = widget
    ? "flex-1 min-w-0 flex flex-col rounded-xl bg-white border border-gray-200/80 shadow-sm overflow-hidden"
    : `flex-1 min-w-0 flex flex-col rounded-2xl bg-white border border-gray-200/80 shadow-sm overflow-hidden ${routeScriptId ? "max-w-5xl h-[85vh]" : ""}`;

  if (widget && embedMode && embedAccessError) {
    return (
      <div className="flex h-full min-h-0 items-center justify-center bg-[#F5F7FA] p-4">
        <div className="max-w-sm rounded-2xl border border-rose-200 bg-white p-5 text-left shadow-sm">
          <h3 className="text-base font-semibold text-gray-900">Embed blocked</h3>
          <p className="mt-2 text-sm leading-6 text-gray-600">{embedAccessError}</p>
        </div>
      </div>
    );
  }

  return (
    <>
      {!widget && (
        <EmbedDeployModal
          open={embedModalOpen}
          onClose={() => setEmbedModalOpen(false)}
          scripts={filteredScripts.length ? filteredScripts : scripts}
          initialScriptId={selectedScriptId || null}
        />
      )}
      {!widget && widgetOpen && createPortal(
        <ChatbotWidget scriptId={popupScriptId} avatarMode={popupAvatarMode} onClose={() => setWidgetOpen(false)} />,
        document.body
      )}
      <div className={rootClass}>
      <div className={innerClass}>
        {/* Left Panel - script/knowledge base selection (hidden in clean /chatbot/:id mode and in widget; script is pre-selected when opening widget) */}
        {!routeScriptId && !widget && (
          <div className={leftPanelClass}>
            <div className="p-4 md:p-5 border-b border-gray-100 shrink-0">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-[#459EA3] to-[#27AAE1] flex items-center justify-center text-white shadow-md shadow-[#459EA3]/20">
                  <MessageSquare className="w-5 h-5" />
                </div>
                <div>
                  <h2 className="text-base font-bold text-gray-900">Select Script</h2>
                  <p className="text-xs text-gray-500">Script or knowledge base</p>
                </div>
              </div>
            </div>
            <div className="flex-1 min-h-0 min-w-0 flex flex-col overflow-hidden p-4 text-left">
              <input
                type="text"
                placeholder="Search by name..."
                className="w-full border border-gray-200 rounded-xl px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-[#459EA3]/50 focus:border-[#459EA3] bg-gray-50/50 shrink-0 mb-3"
                value={searchTerm}
                onChange={(e) => handleSearch(e.target.value)}
              />
              <div className="flex-1 min-h-0 overflow-y-auto space-y-2 mb-4">
                {filteredScripts.length === 0 && (
                  <p className="text-gray-500 text-sm">No scripts found.</p>
                )}
                {filteredScripts.map((s) => (
                  <button
                    key={s._id}
                    type="button"
                    onClick={() => loadScript(s._id)}
                    className={`w-full text-left border rounded-xl p-3 transition-all duration-200 ${
                      selectedScriptId === s._id
                        ? "border-[#459EA3] bg-[#DBF4F3] shadow-sm"
                        : "border-gray-200 hover:border-[#459EA3]/40 hover:bg-gray-50/80"
                    }`}
                  >
                    <div className="flex items-center gap-3">
                      <div className={`w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0 ${
                        selectedScriptId === s._id ? "bg-[#459EA3] text-white" : "bg-gray-100 text-gray-500"
                      }`}>
                        <MessageSquare className="w-4 h-4" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className={`font-semibold text-sm truncate ${selectedScriptId === s._id ? "text-[#2d7a7e]" : "text-gray-900"}`}>
                          {s.scriptName}
                        </p>
                        <p className="text-xs text-gray-500">{s.type || "Conversation Flow"}</p>
                      </div>
                    </div>
                  </button>
                ))}
              </div>
              <div className="shrink-0 mb-4">
                <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1.5">
                  Knowledge Base (collections)
                </label>
                <div className="space-y-1.5 max-h-32 overflow-y-auto overflow-x-visible border border-gray-200 rounded-xl p-2.5 bg-gray-50/50 text-left">
                  {collections.length === 0 ? (
                    <p className="text-xs text-gray-500">No collections. Add files in Knowledge Base first.</p>
                  ) : (
                    collections.map((coll) => {
                      const id = coll._id || coll.id;
                      const sid = String(id);
                      const checked = Array.isArray(selectedCollectionIds) && selectedCollectionIds.includes(sid);
                      return (
                        <label key={sid} className="flex items-start gap-2 cursor-pointer hover:bg-gray-100/80 rounded px-2 py-1 -mx-1 text-left">
                          <input
                            type="checkbox"
                            checked={checked}
                            onChange={(e) => {
                              const ids = [...(selectedCollectionIds || [])];
                              if (e.target.checked) {
                                if (!ids.includes(sid)) ids.push(sid);
                              } else {
                                ids.splice(ids.indexOf(sid), 1);
                              }
                              setSelectedCollectionIds(ids);
                            }}
                            className="rounded border-gray-300 text-[#459EA3] focus:ring-[#459EA3] shrink-0 mt-0.5"
                          />
                          <span className="text-sm text-gray-800 break-words min-w-0 flex-1">{coll.name || coll.collectionName || sid}</span>
                          {coll.fileCount != null && (
                            <span className="text-xs text-gray-500 shrink-0">({coll.fileCount})</span>
                          )}
                        </label>
                      );
                    })
                  )}
                </div>
              </div>

              <div className="flex flex-col gap-2 shrink-0">
                <button
                  onClick={startSession}
                  className="w-full py-2.5 rounded-xl bg-[#459EA3] hover:bg-[#3d8a8e] text-white text-sm font-semibold transition-colors shadow-sm"
                >
                  Start Session
                </button>
                {selectedScriptId && !widget && (
                  <div className="flex flex-col gap-1">
                    <label className="text-xs font-semibold text-gray-500">Open in widget</label>
                    <select
                      value={widgetOpenDropdown}
                      onChange={(e) => {
                        const v = e.target.value;
                        if (v === "") return;
                        setPopupAvatarMode(v);
                        setPopupScriptId(selectedScriptId);
                        setWidgetOpen(true);
                        setWidgetOpenDropdown("");
                      }}
                      className="w-full py-2.5 rounded-xl border-2 border-[#459EA3] bg-white text-[#459EA3] hover:bg-[#DBF4F3] text-sm font-semibold transition-colors focus:outline-none focus:ring-2 focus:ring-[#459EA3]/50"
                    >
                      <option value="">— Choose mode —</option>
                      <option value="none">No Avatar</option>
                      <option value="image">Still image avatar</option>
                      <option value="video">Animated streaming avatar</option>
                      <option value="voice">Voice only (streaming avatar + subtitles)</option>
                    </select>
                  </div>
                )}
                {!widget && (
                  <button
                    type="button"
                    onClick={() => setEmbedModalOpen(true)}
                    className="w-full py-2.5 rounded-xl border-2 border-[#459EA3] bg-white text-[#459EA3] hover:bg-[#DBF4F3] text-sm font-semibold transition-colors flex items-center justify-center gap-2"
                  >
                    <Code className="w-4 h-4" />
                    Deploy embedding
                  </button>
                )}
                <button
                  onClick={resetSelections}
                  className="w-full py-2 rounded-xl border border-gray-200 text-gray-600 hover:bg-gray-50 text-sm font-medium transition-colors"
                >
                  Clear Selections
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Chat panel */}
        <div className={chatPanelClass}>
          {/* Top bar: minimal for voice-only (just close), full for others */}
          <div
            className={`shrink-0 relative flex items-center justify-between text-white ${widgetVoiceOnly ? "px-2 py-1.5 bg-black/50" : "px-4 md:px-5 py-2.5"}`}
            style={widget && !widgetVoiceOnly ? { background: `linear-gradient(to right, ${embedPrimaryColor}, ${darkenHex(embedPrimaryColor)})` } : widgetVoiceOnly ? undefined : { background: "linear-gradient(to right, #459EA3, #3d8a8e)" }}
          >
            {!widgetVoiceOnly && (
            <div className="flex items-center gap-2 min-w-0">
              <MessageSquare className="w-5 h-5 opacity-90 flex-shrink-0" />
              <h3 className="font-semibold text-base truncate">{widget ? embedTitle : "Chat with Maya"}</h3>
            </div>
            )}
            {widgetVoiceOnly && <div />}
            {!(widget && (widgetAvatarMode === "video" || widgetVoiceOnly)) && !widgetVoiceOnly && (
              <label className="flex items-center gap-2 cursor-pointer absolute left-1/2 -translate-x-1/2">
                <span className="text-xs md:text-sm text-white/90">Auto-play</span>
                <div className="relative inline-block w-10 h-5">
                  <input
                    type="checkbox"
                    checked={autoPlayEnabled}
                    onChange={(e) => setAutoPlayEnabled(e.target.checked)}
                    className="sr-only peer"
                  />
                  <div className="w-10 h-5 bg-white/30 rounded-full peer peer-checked:bg-white/90 after:content-[''] after:absolute after:top-0.5 after:left-0.5 after:bg-white after:rounded-full after:h-4 after:w-4 after:transition-all peer-checked:after:translate-x-5"></div>
                </div>
              </label>
            )}
            {widget && onClose ? (
              <button
                type="button"
                onClick={onClose}
                className={`rounded-lg transition-colors flex-shrink-0 ${widgetVoiceOnly ? "p-1.5 hover:bg-white/20" : "p-2 hover:bg-white/20"}`}
                aria-label="Close"
              >
                <X className={widgetVoiceOnly ? "w-4 h-4" : "w-5 h-5"} />
              </button>
            ) : (
              <div className="w-9" />
            )}
          </div>

          {/* Avatar: when avatarMode is 'image' or 'video' (main page), or in widget when widgetAvatarMode is not 'none' or widgetVoiceOnly */}
          {((!widget && avatarMode !== "none") || (widget && (widgetAvatarMode !== "none" || widgetVoiceOnly))) && !(widget && widgetVoiceOnly) && (
            <div className={`flex-[0_0_33.333%] min-h-0 flex flex-col bg-gradient-to-b from-[#e0f2f1] to-[#f0fdfa] border-b border-gray-200/80 ${widget ? "items-stretch justify-stretch" : "items-center justify-center p-3"}`}>
              <div className={widget ? "w-full h-full min-h-0 flex items-center justify-center" : "w-full h-full max-w-md mx-auto flex items-center justify-center min-h-0"}>
                <TalkingAvatar
                  isSpeaking={playingAudioId !== null}
                  imageUrl={(!widget && avatarMode === "image") || (widget && widgetAvatarMode === "image") ? AvatarStill : null}
                  stream={(!widget && avatarMode === "video") || (widget && widgetAvatarMode === "video") ? avatarStream : null}
                  videoUrl={(!widget && avatarMode === "video") || (widget && widgetAvatarMode === "video") ? avatarVideoUrl : null}
                  onVideoEnded={() => setPlayingAudioId(null)}
                  size={140}
                  fillContainer={!!(avatarStream || avatarVideoUrl) || avatarMode === "image" || widgetAvatarMode === "image" || widgetAvatarMode === "video"}
                  className={avatarStream || avatarVideoUrl || avatarMode === "image" || widgetAvatarMode === "image" || widgetAvatarMode === "video" ? "w-full h-full max-h-full" : ""}
                />
              </div>
            </div>
          )}

          {/* Voice-only widget: single block = avatar with subtitles overlaid on it + mic */}
          {widget && widgetVoiceOnly ? (
            <>
              <div className="flex-1 min-h-0 relative flex flex-col bg-black overflow-hidden">
                <div className="absolute inset-0 flex items-center justify-center">
                  <TalkingAvatar
                    isSpeaking={playingAudioId !== null}
                    stream={avatarStream}
                    videoUrl={avatarVideoUrl}
                    onVideoEnded={() => setPlayingAudioId(null)}
                    size={140}
                    fillContainer
                    className="w-full h-full max-h-full"
                  />
                </div>
                {/* Subtitles + mic: fixed-height status area so the mic never shifts when "Listening…" appears */}
                <div className="absolute bottom-0 left-0 right-0 pt-6 pb-2 px-3 bg-gradient-to-t from-black/85 via-black/40 to-transparent flex flex-col items-center gap-2">
                  <div className="h-[5.25rem] w-full flex flex-col justify-end items-center text-center pointer-events-none space-y-0.5 overflow-hidden">
                    {isListening && (
                      <p className="text-xs font-medium text-white drop-shadow-lg animate-pulse shrink-0">Listening…</p>
                    )}
                    {sending && !isListening && (
                      <p className="text-xs text-white/90 drop-shadow-lg shrink-0">Thinking…</p>
                    )}
                    {!isListening && !sending && (() => {
                      const lastUser = [...messages].reverse().find((m) => m.role === "user");
                      const lastAssistant = [...messages].reverse().find((m) => m.role === "assistant");
                      return (
                        <div className="w-full space-y-0.5 flex flex-col justify-end min-h-0">
                          {lastUser && (
                            <p className="text-xs text-white drop-shadow-lg line-clamp-2">
                              <span className="font-semibold text-white/90">You: </span>
                              {lastUser.content}
                            </p>
                          )}
                          {lastAssistant && (
                            <p className="text-xs text-white drop-shadow-lg line-clamp-2">
                              <span className="font-semibold text-white/90">Maya: </span>
                              {lastAssistant.content}
                            </p>
                          )}
                          {messages.length === 0 && (
                            <p className="text-xs text-white/70 drop-shadow-lg">Hold mic to speak</p>
                          )}
                        </div>
                      );
                    })()}
                  </div>
                  <button
                    onMouseDown={startListening}
                    onMouseUp={stopListening}
                    onTouchStart={(e) => { e.preventDefault(); startListening(); }}
                    onTouchEnd={(e) => { e.preventDefault(); stopListening(); }}
                    disabled={sending || (!selectedScriptData && !(selectedCollectionIds?.length) && !routeScriptId && messages.length === 0)}
                    className={`p-2.5 rounded-full transition-colors shadow-lg pointer-events-auto shrink-0 ${
                      isListening ? "bg-red-500 text-white animate-pulse" : "bg-white/95 text-gray-700 hover:bg-white"
                    } disabled:opacity-50 disabled:cursor-not-allowed`}
                    title={isListening ? "Release to send" : "Hold to speak"}
                  >
                    <Mic size={22} />
                  </button>
                </div>
              </div>
            </>
          ) : (
            <>
              {/* Messages - off-white background */}
              <div className="flex-1 min-h-0 overflow-y-auto p-4 space-y-4 bg-[#F9F9F9]">
                {messages.length === 0 && (
                  <div className="flex items-center justify-center h-full min-h-[200px]">
                    <div className="text-center">
                      <div className="w-14 h-14 rounded-2xl bg-gray-200/60 flex items-center justify-center mx-auto mb-3">
                        <MessageSquare className="w-7 h-7 text-gray-400" />
                      </div>
                      <p className="text-sm text-gray-500">
                        {routeScriptId ? "Type your first message to begin…" : "Start a session to send the first message."}
                      </p>
                    </div>
                  </div>
                )}
                {messages.map((m, i) => (
                  <div key={i} className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`}>
                    <div
                      className={`max-w-[85%] rounded-2xl p-3 md:p-4 ${
                        m.role === "user"
                          ? "text-white shadow-md"
                          : "bg-white text-gray-800 border border-gray-200/80 shadow-sm"
                      }`}
                      style={m.role === "user" ? (widget ? { background: `linear-gradient(to right, ${embedPrimaryColor}, ${darkenHex(embedPrimaryColor)})` } : { background: "linear-gradient(to right, #459EA3, #3d8a8e)" }) : undefined}
                    >
                      <div className="flex flex-col gap-1">
                        <div className="flex items-start gap-3">
                          <div className="flex-1 whitespace-pre-wrap break-words text-sm">{m.content}</div>
                          {m.role === "assistant" && (
                            <button
                            onClick={() => playAIResponse(m.content, i)}
                            className={`flex-shrink-0 p-2 rounded-xl transition ${
                              playingAudioId === i ? "text-white" : "bg-gray-100 text-gray-600 hover:bg-gray-200"
                            }`}
                            style={playingAudioId === i && widget ? { backgroundColor: embedPrimaryColor } : playingAudioId === i ? { backgroundColor: "#459EA3" } : undefined}
                            title={playingAudioId === i ? "Stop reading" : "Read aloud"}
                          >
                            <Volume2 size={16} />
                          </button>
                        )}
                        </div>
                        {m.role === "assistant" && m.rag?.hadKb && (
                          <div className="text-xs text-gray-500 mt-1 flex items-center gap-1.5">
                            {m.rag.usedRag ? (
                              <>
                                <Search size={12} className="shrink-0" />
                                <span>
                                  Used Knowledge Base ({m.rag.chunkCount} chunk{m.rag.chunkCount !== 1 ? "s" : ""}
                                  {m.rag.sources?.length ? ` from ${m.rag.sources.slice(0, 2).join(", ")}${m.rag.sources.length > 2 ? "…" : ""}` : ""})
                                </span>
                              </>
                            ) : (
                              <span>No relevant chunks found in Knowledge Base</span>
                            )}
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                ))}
                {sending && (
                  <div className="flex justify-start">
                    <div className="bg-white border border-gray-200/80 rounded-2xl px-4 py-3 shadow-sm">
                      <div className="flex items-center gap-2">
                        <div className="w-2 h-2 rounded-full animate-bounce" style={{ backgroundColor: widget ? embedPrimaryColor : "#459EA3" }} />
                        <div className="w-2 h-2 rounded-full animate-bounce" style={{ animationDelay: "0.2s", backgroundColor: widget ? embedPrimaryColor : "#459EA3" }} />
                        <div className="w-2 h-2 rounded-full animate-bounce" style={{ animationDelay: "0.4s", backgroundColor: widget ? embedPrimaryColor : "#459EA3" }} />
                        <span className="ml-2 text-sm text-gray-600">Thinking…</span>
                      </div>
                    </div>
                  </div>
                )}
                <div ref={messagesEndRef} />
              </div>

              {/* Input area */}
              <div className="shrink-0 p-4 border-t border-gray-200/80 bg-white">
                <div className="flex gap-2">
                  <input
                    type="text"
                    placeholder={
                      !selectedScriptData && !(selectedCollectionIds?.length) && !routeScriptId
                        ? "Select a script or document first…"
                        : isListening
                          ? "Listening..."
                          : messages.length === 0
                            ? "Type your message to begin…"
                            : "Type your message…"
                    }
                    className="flex-1 border border-gray-200 rounded-xl px-4 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-[#459EA3]/50 focus:border-[#459EA3]"
                    value={input}
                    onChange={(e) => setInput(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === "Enter" && !e.shiftKey) {
                        e.preventDefault();
                        sendMessage();
                      }
                    }}
                    disabled={sending || isListening || (!selectedScriptData && !(selectedCollectionIds?.length) && !routeScriptId && messages.length === 0)}
                  />
                  <button
                    onMouseDown={startListening}
                    onMouseUp={stopListening}
                    onTouchStart={(e) => { e.preventDefault(); startListening(); }}
                    onTouchEnd={(e) => { e.preventDefault(); stopListening(); }}
                    disabled={sending || (!selectedScriptData && !(selectedCollectionIds?.length) && !routeScriptId && messages.length === 0)}
                    className={`p-3 rounded-xl transition shrink-0 ${
                      isListening ? "bg-red-500 text-white animate-pulse" : "bg-gray-100 text-gray-600 hover:bg-gray-200"
                    } disabled:opacity-50 disabled:cursor-not-allowed`}
                    title={isListening ? "Release to stop" : "Hold to speak"}
                  >
                    <Mic size={20} />
                  </button>
                  <button
                    onClick={() => sendMessage()}
                    disabled={!input.trim() || sending || isListening || (!selectedScriptData && !(selectedCollectionIds?.length) && !routeScriptId && messages.length === 0)}
                    className="p-3 rounded-xl text-white disabled:opacity-50 disabled:cursor-not-allowed transition inline-flex items-center gap-2 shrink-0"
                    style={widget ? { backgroundColor: embedPrimaryColor } : { backgroundColor: "#459EA3" }}
                  >
                    <Send size={20} />
                    {!routeScriptId && <span className="hidden sm:inline text-sm font-medium">Send</span>}
                  </button>
                </div>
                <div className="mt-2 flex justify-end">
                  <button
                    type="button"
                    onClick={endSession}
                    disabled={sending || isListening || messages.length <= 1}
                    className="px-3 py-2 rounded-xl border border-gray-300 text-gray-700 hover:bg-gray-50 text-sm font-medium transition inline-flex items-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
                    title="End current session and save chatbot log"
                  >
                    <RotateCcw size={16} />
                    End Session & Save Log
                  </button>
                </div>
                <p
                  className={`text-xs mt-2 text-center font-medium min-h-[2.75rem] flex items-center justify-center ${isListening ? "text-red-600" : "text-transparent select-none"}`}
                  aria-live="polite"
                  aria-hidden={!isListening}
                >
                  {isListening ? "🎤 Listening… Release to send" : "\u00a0"}
                </p>
              </div>
            </>
          )}
        </div>
      </div>
      </div>
    </>
  );
};

export default Chatbot;
